import { useEffect, useRef, useState } from "react"
import { Loader2 } from "lucide-react"

import AuthAlert from "@/components/tuw/auth/AuthAlert"
import { AuthHeading } from "@/components/tuw/auth/AuthHeading"
import BrandLockup from "@/components/tuw/auth/BrandLockup"
import { PasswordField } from "@/components/tuw/auth/fields"
import { AccountExpired, WrongAccount } from "@/components/tuw/openHouse/WaitingScreen"
import { Button } from "@/components/ui/button"
import { ENROLLMENT_CONFIRMATION as E } from "@/lib/openHouse/enrollmentWording"
import { confirmEnrollmentByLink } from "@/lib/openHouse/guest"
import { CONFIRMATION as C } from "@/lib/openHouse/wording"
import { SUPPORT_HREF } from "@/lib/viewingRoom/enrollment"
import { VR } from "@/lib/viewingRoom/wording"
import { takeToken } from "./OpenHouseConfirm"

// Account-only enrollment's link page (Viewing Room VR-7), served by
// OpenHouse::EnrollmentsController#show at /join/confirm: Key turn's link page
// (OpenHouseConfirm) for a standalone account, with no project, price or
// deadline. The token arrives the same way and is posted with the account's
// password; confirmed, the page goes where the server says.
//
// `links` is the server's { signIn }. An expired link ends in the deleted
// account's screen (AccountExpired); an invalid one returns to
// the waiting screen's own page (WAITING_PATH, ViewingRoomWaiting), which
// sends another email; an account conflict links to support.

const WAITING_PATH = "/join/account"

// Refusals that end the form, by the server's reason.
const ENDS = {
  link_invalid: "invalid", link_expired: "expired", expired: "expired", post_expired: "expired",
  already_confirmed: "already", wrong_account: "wrongAccount",
}

// Refusals shown above the form.
function alertFor({ reason, data }) {
  if (reason === "password_locked") return E.linkPasswordLimited
  if (reason === "too_many_attempts") return E.confirmationLimited(data?.retry_after_seconds)
  if (reason === "not_pending") return E.noPendingAccount
  if (reason === "account_conflict") return E.accountConflict
  return E.failed
}

export default function OpenHouseEnrollmentConfirm({ links }) {
  const token = useRef(null)
  const [view, setView] = useState(null)
  const [password, setPassword] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [fieldError, setFieldError] = useState(null)
  const [alert, setAlert] = useState(null)
  const [support, setSupport] = useState(false)

  useEffect(() => {
    if (token.current === null) token.current = takeToken()
    setView(token.current ? "form" : "invalid")
  }, [])

  const submit = async (event) => {
    event.preventDefault()
    if (submitting || !password) return

    setSubmitting(true)
    setFieldError(null)
    setAlert(null)
    try {
      const { redirect } = await confirmEnrollmentByLink(token.current, password)
      window.location.assign(redirect)
    } catch (err) {
      setSubmitting(false)
      if (ENDS[err.reason]) return setView(ENDS[err.reason])
      if (err.reason === "password_incorrect") return setFieldError(E.linkPasswordIncorrect)

      setSupport(err.reason === "account_conflict")
      setAlert(alertFor(err))
    }
  }

  let body = null
  if (view === "expired") {
    body = <AccountExpired oldConfirmation />
  } else if (view === "invalid") {
    body = (
      <>
        <AuthAlert tone="bad">{E.linkInvalid}</AuthAlert>
        <Button asChild variant="outline">
          <a href={WAITING_PATH}>{C.return}</a>
        </Button>
      </>
    )
  } else if (view === "already") {
    // The account itself, signed in, is sent on, so whoever reads this is not signed in here.
    body = (
      <>
        <AuthHeading title={E.alreadyConfirmedTitle}>{E.confirmedElsewhere}</AuthHeading>
        <Button asChild>
          <a href={links.signIn}>{E.signInContinue}</a>
        </Button>
      </>
    )
  } else if (view === "wrongAccount") {
    body = <WrongAccount />
  } else if (view === "form") {
    body = (
      <form onSubmit={submit} noValidate className="flex flex-col gap-[22px]">
        <AuthHeading title={E.linkTitle}>{E.linkBody}</AuthHeading>
        {alert && (
          <AuthAlert tone="bad">
            {alert}
            {support && (
              <a href={SUPPORT_HREF} className="mt-1 block font-semibold underline underline-offset-2">
                {VR.confirmation.contactSupport}
              </a>
            )}
          </AuthAlert>
        )}
        <PasswordField id="vr-confirm-password" label={E.linkPassword} autoComplete="current-password"
          value={password} onChange={(e) => setPassword(e.target.value)} error={fieldError} />
        <Button type="submit" disabled={submitting || !password} className="gap-1.5">
          {submitting && <Loader2 className="size-4 animate-spin" />}
          {submitting ? E.submitting : E.submit}
        </Button>
      </form>
    )
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border bg-card px-8 py-3.5 max-[561px]:px-4">
        <BrandLockup className="h-[26px]" />
      </header>
      <main className="flex justify-center px-4 py-12">
        <div className="flex w-full max-w-[400px] flex-col gap-[22px]">{body}</div>
      </main>
    </div>
  )
}
